import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/apiClient";
import { STATUSES } from "../lib/statusConfig";
import { BUCKETS } from "../lib/buckets";
import { MultiSelect } from "../components/MultiSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { BarChart3, Filter, X, Download } from "lucide-react";

const DEPARTMENTS = [
  { value: "ramp", label: "Ramp" },
  { value: "ops", label: "Operations/Cargo" },
  { value: "provisioning", label: "Provisioning" },
];
const DEPT_LABEL = Object.fromEntries(DEPARTMENTS.map((d) => [d.label, d.value]));

const EMPTY = { division: [], discipline: [], status: [], bucket: [], subject: [], specialist: [], grievant: "", exclude_grievant: [], date_from: "", date_to: "" };

function Breakdown({ title, rows }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="border border-border rounded-sm bg-card p-5" data-testid={`report-breakdown-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
      <h3 className="font-head text-sm font-extrabold uppercase tracking-[0.15em] text-muted-foreground mb-4">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No data.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.name} data-testid={`report-row-${r.name}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="truncate pr-2">{r.name}</span>
                <span className="tabular-nums font-medium shrink-0">{r.value.toLocaleString()} · {r.pct}%</span>
              </div>
              <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${(r.value / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [filters, setFilters] = useState(EMPTY);
  const [disciplines, setDisciplines] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [grievantNames, setGrievantNames] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/options").then((r) => {
      setDisciplines(r.data.disciplines || []);
      setSubjects(r.data.subjects || []);
      setSpecialists(r.data.specialists || []);
    }).catch(() => {});
    api.get("/grievant-options").then((r) => {
      const names = [...new Set((r.data || []).map((o) => o.name).filter(Boolean))].sort();
      setGrievantNames(names);
    }).catch(() => {});
  }, []);

  const buildParams = () => {
    const params = {};
    if (filters.division.length) params.division = filters.division.map((l) => DEPT_LABEL[l] || l).join(",");
    if (filters.discipline.length) params.discipline = filters.discipline.join(",");
    if (filters.status.length) params.status = filters.status.join(",");
    if (filters.bucket.length) params.bucket = filters.bucket.map((l) => (BUCKETS.find((b) => b.label === l)?.value) || l).join(",");
    if (filters.subject.length) params.subject = filters.subject.join(",");
    if (filters.specialist.length) params.specialist = filters.specialist.join(",");
    if (filters.grievant.trim()) params.grievant = filters.grievant.trim();
    if (filters.exclude_grievant.length) params.exclude_grievant = filters.exclude_grievant.join(",");
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return params;
  };

  useEffect(() => {
    setLoading(true);
    const params = buildParams();
    const t = setTimeout(() => {
      api.get("/reports", { params })
        .then((r) => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/export", { params: buildParams(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `grievance_report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* ignore */ } finally { setExporting(false); }
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const activeCount = useMemo(() => {
    let n = 0;
    ["division", "discipline", "status", "bucket", "subject", "specialist"].forEach((k) => { if (filters[k].length) n++; });
    if (filters.grievant.trim()) n++;
    if (filters.exclude_grievant.length) n++;
    if (filters.date_from) n++;
    if (filters.date_to) n++;
    return n;
  }, [filters]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="reports-page">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-2">
        <BarChart3 className="w-4 h-4" /> Reports
      </p>
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Grievance Reports</h1>
      <p className="text-sm text-muted-foreground mt-1.5">Filter grievances across all departments and view totals with percentages.</p>

      <div className="mt-6 border border-border rounded-sm bg-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters</span>
          <span className="text-xs text-muted-foreground">(multi-select)</span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="reports-export" onClick={exportCsv} disabled={exporting}>
              <Download className="w-3.5 h-3.5 mr-1" /> {exporting ? "Exporting…" : "Export CSV"}
            </Button>
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" data-testid="reports-reset" onClick={() => setFilters(EMPTY)}>
                <X className="w-3.5 h-3.5 mr-1" /> Reset
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-[11px] text-muted-foreground">Department</label>
            <div className="mt-1"><MultiSelect testid="filter-department" placeholder="All Departments"
              value={filters.division} onChange={(v) => set("division", v)} options={DEPARTMENTS.map((d) => d.label)} /></div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Board</label>
            <div className="mt-1"><MultiSelect testid="filter-board" placeholder="All Boards"
              value={filters.bucket} onChange={(v) => set("bucket", v)} options={BUCKETS.map((b) => b.label)} /></div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Discipline / Letter Level</label>
            <div className="mt-1"><MultiSelect testid="filter-discipline" placeholder="All"
              value={filters.discipline} onChange={(v) => set("discipline", v)} options={disciplines} /></div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Status</label>
            <div className="mt-1"><MultiSelect testid="filter-status" placeholder="All Statuses"
              value={filters.status} onChange={(v) => set("status", v)} options={STATUSES} /></div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Subject</label>
            <div className="mt-1"><MultiSelect testid="filter-subject" placeholder="All Subjects"
              value={filters.subject} onChange={(v) => set("subject", v)} options={subjects} /></div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">District Rep / Specialist</label>
            <div className="mt-1"><MultiSelect testid="filter-specialist" placeholder="All Specialists"
              value={filters.specialist} onChange={(v) => set("specialist", v)} options={specialists} /></div>
          </div>
          <div className="lg:col-span-1">
            <label className="text-[11px] text-muted-foreground">Grievant Name</label>
            <Input data-testid="filter-grievant" placeholder="Search name…" value={filters.grievant}
              onChange={(e) => set("grievant", e.target.value)} className="mt-1 rounded-sm" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Exclude Grievants</label>
            <div className="mt-1"><MultiSelect testid="filter-exclude-grievant" placeholder="None excluded"
              value={filters.exclude_grievant} onChange={(v) => set("exclude_grievant", v)}
              options={grievantNames} searchPlaceholder="Search grievant to exclude…" /></div>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Due From</label>
            <Input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} className="mt-1 rounded-sm" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground">Due To</label>
            <Input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} className="mt-1 rounded-sm" />
          </div>
        </div>
      </div>

      <div className="mt-6 border border-border rounded-sm bg-card p-6 flex items-baseline gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Matching Grievances</div>
          <div className="font-head text-5xl font-extrabold tracking-tight tabular-nums mt-1" data-testid="reports-total">
            {loading ? "…" : (data?.total ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {activeCount === 0 ? "All grievances (no filters applied)" : `${activeCount} filter${activeCount > 1 ? "s" : ""} applied`}
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <Breakdown title="By Status" rows={data?.by_status || []} />
        <Breakdown title="By Discipline / Letter Level" rows={data?.by_discipline || []} />
        <Breakdown title="By Department" rows={data?.by_division || []} />
        <Breakdown title="By Board" rows={data?.by_bucket || []} />
        <Breakdown title="By District Rep / Specialist" rows={data?.by_specialist || []} />
      </div>
    </div>
  );
}
